import { useCallback } from "react"
import { useQueryClient } from "@tanstack/react-query"
import type { AxiosInstance } from "axios"

import { useApiMutation } from "../../../../../core/api/useApiMutation"
import { queryKeyFor } from "../../../../../core/api/useApiQuery"
import { type DateTimeFactory, useDateTime } from "../../../../localization/useDateTime"

import type { NarrowedAvailability } from "../productGroupAvailability"
import { toCreateProduct, toProductFormValues } from "../productFormValues"
import { MerchantProductsQuery, getProductDetails, updateProduct } from "../productsApi"

type NarrowProductAvailabilityParams = Readonly<{
    productId: number
    narrowedAvailability: NarrowedAvailability
    dateTimeFactory: DateTimeFactory
}>

// the update endpoint takes the whole product, so it is saved the same way the edit page saves it
async function narrowProductAvailability(
    { productId, narrowedAvailability, dateTimeFactory }: NarrowProductAvailabilityParams,
    axios: AxiosInstance,
) {
    const { product } = await getProductDetails({ productId }, axios)
    const values = toProductFormValues(product, dateTimeFactory)
    const narrowedProduct = toCreateProduct(
        {
            ...values,
            availabilityDetails: {
                ...values.availabilityDetails,
                weekDays: narrowedAvailability.weekDays,
                timeInterval: narrowedAvailability.timeInterval ?? { start: null, end: null },
            },
        },
        dateTimeFactory,
    )

    return updateProduct(
        {
            productId,
            product: {
                ...narrowedProduct,
                // the edit form carries only the first price list; keep them all with their adjusted prices
                ...(product.priceLists?.length
                    ? { priceLists: product.priceLists.map(({ id, adjustedPrice }) => ({ id, adjustedPrice })) }
                    : {}),
            },
        },
        axios,
    )
}

type ProductAvailabilityToNarrow = Readonly<{
    productId: number
    narrowedAvailability: NarrowedAvailability
}>

export function useNarrowProductAvailability() {
    const dateTimeFactory = useDateTime()
    const queryClient = useQueryClient()

    const { mutateAsync } = useApiMutation(narrowProductAvailability, {
        async onSuccess(params, _, context) {
            await context.queryClient.invalidateQueries({
                queryKey: queryKeyFor(MerchantProductsQuery.PRODUCT_DETAILS, { productId: params.productId }),
            })
        },
    })

    // failures are shown by the mutation, the products list is refreshed once for all of them
    return useCallback(
        async (products: ReadonlyArray<ProductAvailabilityToNarrow>) => {
            await Promise.allSettled(
                products.map(({ productId, narrowedAvailability }) =>
                    mutateAsync({ productId, narrowedAvailability, dateTimeFactory }),
                ),
            )
            await queryClient.invalidateQueries({ queryKey: [MerchantProductsQuery.LIST] })
        },
        [mutateAsync, dateTimeFactory, queryClient],
    )
}
